import { yupResolver } from '@hookform/resolvers/yup';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useSettingsStore } from 'common/stores';
import { CodeInput } from 'core/components/form';
import { CircularIndicator } from 'core/components/uikit';
import { DisclosureWithTitle } from 'features/components';

import { ChangePinCodeFormData, changePinCodeSchema } from './validationSchema';

const defaultValues = {
  newPin: '',
  prevPin: '',
  confirmNewPin: '',
};

const ChangePinForm = () => {
  const { t } = useTranslation();

  const {
    reset,
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm<ChangePinCodeFormData>({
    resolver: yupResolver(changePinCodeSchema),
    mode: 'all',
    defaultValues,
  });

  const isButtonDisabled = !isValid || isSubmitting;

  const onSubmit = handleSubmit(async (values) => {
    const { oldPin, newPin } = values;
    const { updatePin } = useSettingsStore.getState();

    const success = await updatePin({
      oldPIN: oldPin,
      newPIN: newPin,
    });

    if (success) {
      reset(defaultValues);
      enqueueSnackbar(t('pages.settings.changePinForm.success'), {
        variant: 'success',
        hideIconVariant: true,
      });
    }
  });

  return (
    <DisclosureWithTitle
      title={<h4>{t('pages.settings.changePinForm.title')}</h4>}
      defaultOpen
    >
      <form
        className="flex max-w-full flex-col gap-2 sm:max-w-80"
        onSubmit={onSubmit}
      >
        <CodeInput
          name="oldPin"
          control={control}
          componentProps={{
            numInputs: 4,
            label: t('pages.settings.changePinForm.fields.oldPin.label'),
          }}
        />
        <CodeInput
          name="newPin"
          control={control}
          componentProps={{
            numInputs: 4,
            label: t('pages.settings.changePinForm.fields.newPin.label'),
          }}
        />
        <CodeInput
          name="confirmNewPin"
          control={control}
          componentProps={{
            numInputs: 4,
            label: t('pages.settings.changePinForm.fields.confirmNewPin.label'),
          }}
        />
        <button type="submit" disabled={isButtonDisabled}>
          {isSubmitting ? <CircularIndicator /> : t('common.save')}
        </button>
      </form>
    </DisclosureWithTitle>
  );
};

export default ChangePinForm;
